import { useEffect, useState } from 'react';

/**
 * The artwork on the sign-in panel.
 *
 * Drawn as inline SVG rather than fetched as images, for four reasons that all
 * matter here:
 *
 *  - **Transparent by construction.** There is no background to remove; the
 *    gradient behind shows through because nothing is painted over it.
 *  - **No network request.** The console's content-security-policy allows
 *    images from this origin only, so a remote meme would be blocked — and a
 *    sign-in page that waits on somebody else's CDN is a sign-in page that
 *    hangs when they have an outage.
 *  - **Nothing to license.** Real coding memes are somebody's copyright. These
 *    are drawn here, so nobody has to wonder.
 *  - **They scale.** The same file is crisp on a cracked phone screen and a
 *    27-inch monitor.
 *
 * The subjects are the trade rather than generic technology: a splice tray, a
 * pole run, an ONT, a topology. Two of them are jokes an engineer would
 * recognise, because the panel should feel like it belongs to the people who
 * use it.
 */

const ROTATE_MS = 9_000;

type Art = { id: string; caption: string; draw: () => JSX.Element };

/** Everything inherits `currentColor`, so one palette drives all of them. */
const ART: Art[] = [
  {
    id: 'fibre',
    caption: 'Fusion splice, 0.02 dB. On the first try, allegedly.',
    draw: () => (
      <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <path d="M20 90 C 60 90, 70 60, 100 60" opacity="0.55" />
        <path d="M20 100 C 70 100, 80 70, 100 70" opacity="0.75" />
        <path d="M20 110 C 60 110, 70 80, 100 80" opacity="0.55" />
        <rect x="100" y="52" width="40" height="36" rx="6" opacity="0.9" />
        <path d="M140 60 C 170 60, 180 90, 220 90" opacity="0.55" />
        <path d="M140 70 C 170 70, 180 100, 220 100" opacity="0.75" />
        <path d="M140 80 C 170 80, 180 110, 220 110" opacity="0.55" />
        <circle cx="120" cy="70" r="4" fill="currentColor" stroke="none" />
        <circle cx="120" cy="70" r="11" opacity="0.4" />
      </g>
    ),
  },
  {
    id: 'pole',
    caption: 'Aerial run to Egesa. Mind the wind.',
    draw: () => (
      <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <path d="M40 140 L40 40 M20 140 L60 140" opacity="0.9" />
        <path d="M22 56 L58 56 M26 72 L54 72" opacity="0.8" />
        <path d="M200 140 L200 40 M180 140 L220 140" opacity="0.9" />
        <path d="M182 56 L218 56 M186 72 L214 72" opacity="0.8" />
        <path d="M40 56 C 90 84, 150 84, 200 56" opacity="0.6" />
        <path d="M40 72 C 90 100, 150 100, 200 72" opacity="0.45" />
        <circle cx="120" cy="80" r="5" fill="currentColor" stroke="none" opacity="0.9" />
      </g>
    ),
  },
  {
    id: 'topology',
    caption: 'One OLT, sixteen homes, no argument.',
    draw: () => (
      <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <rect x="16" y="76" width="46" height="34" rx="7" opacity="0.95" />
        <path d="M62 93 L104 93" opacity="0.7" />
        <circle cx="118" cy="93" r="14" opacity="0.95" />
        <path d="M132 93 L166 55 M132 93 L166 93 M132 93 L166 131" opacity="0.6" />
        {[55, 93, 131].map((y) => (
          <rect key={y} x="166" y={y - 13} width="40" height="26" rx="6" opacity="0.85" />
        ))}
        <circle cx="39" cy="93" r="4" fill="currentColor" stroke="none" />
      </g>
    ),
  },
  {
    id: 'ont',
    caption: 'Green light means green light.',
    draw: () => (
      <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <rect x="52" y="60" width="136" height="66" rx="12" opacity="0.95" />
        <path d="M70 82 L170 82" opacity="0.35" />
        {[0, 1, 2, 3].map((i) => (
          <circle key={i} cx={74 + i * 22} cy={104} r="5" fill="currentColor" stroke="none"
            opacity={i === 0 ? 1 : 0.35} />
        ))}
        <path d="M188 93 C 214 93, 214 60, 232 60" opacity="0.6" />
        <path d="M52 93 C 26 93, 26 126, 8 126" opacity="0.6" />
      </g>
    ),
  },
  {
    id: 'works-here',
    caption: '“It works at the exchange.” — every ticket, ever.',
    draw: () => (
      <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <rect x="24" y="56" width="82" height="60" rx="9" opacity="0.9" />
        <path d="M40 76 L74 76 M40 90 L88 90 M40 104 L62 104" opacity="0.5" />
        <path d="M114 86 L154 86" opacity="0.8" />
        <path d="M142 76 L154 86 L142 96" opacity="0.8" />
        <rect x="162" y="56" width="82" height="60" rx="9" opacity="0.9" />
        <path d="M186 78 L220 106 M220 78 L186 106" opacity="0.85" />
      </g>
    ),
  },
  {
    id: 'ninety-nine',
    caption: '99 little bugs in the trunk. Patch one round, 127 to go.',
    draw: () => (
      <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <ellipse cx="134" cy="90" rx="34" ry="26" opacity="0.95" />
        <path d="M134 64 L134 116" opacity="0.6" />
        <path d="M100 74 L74 60 M100 90 L70 90 M100 106 L74 120" opacity="0.7" />
        <path d="M168 74 L194 60 M168 90 L198 90 M168 106 L194 120" opacity="0.7" />
        <circle cx="124" cy="72" r="3.5" fill="currentColor" stroke="none" />
        <circle cx="144" cy="72" r="3.5" fill="currentColor" stroke="none" />
        <path d="M120 46 L128 60 M148 46 L140 60" opacity="0.7" />
      </g>
    ),
  },
];

export function TechArt({ className = '' }: { className?: string }) {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * ART.length));
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Somebody who has asked for less movement gets a still picture. The panel
    // is decoration; nothing here is worth overriding that preference for.
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (still) return;

    const timer = window.setInterval(() => {
      // Fade out, swap, fade in — so the change reads as one picture replacing
      // another rather than a flicker.
      setVisible(false);
      window.setTimeout(() => {
        setIndex((current) => (current + 1) % ART.length);
        setVisible(true);
      }, 420);
    }, ROTATE_MS);

    return () => window.clearInterval(timer);
  }, []);

  const art = ART[index]!;

  return (
    <div className={`flex flex-col items-center gap-4 ${className}`}>
      <svg
        viewBox="0 0 256 170"
        className={`h-44 w-full max-w-sm transition-opacity duration-500 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        role="img"
        aria-label={art.caption}
      >
        {art.draw()}
      </svg>

      <p
        className={`max-w-xs text-center text-sm transition-opacity duration-500 ${
          visible ? 'opacity-80' : 'opacity-0'
        }`}
      >
        {art.caption}
      </p>
    </div>
  );
}
